import * as React from "react";
import {
  Link,
  createFileRoute,
  stripSearchParams,
} from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  CheckIcon,
  ChevronsUpDownIcon,
  EyeIcon,
  ImportIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  UploadIcon,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { HTTPError } from "ky";
import type { ColumnDef } from "@tanstack/react-table";
import type {
  User,
  UserFormData,
  UserUpdateFormData,
} from "@/lib/queries/users";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useUsersList,
  userFormSchema,
  userUpdateFormSchema,
  usersListQueryOptions,
} from "@/lib/queries/users";
import { useDepartmentsAll } from "@/lib/queries/departments";
import { useRolesAll } from "@/lib/queries/roles";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/http";
import { generateDefaultValue } from "@/lib/utils/zod";
import { PasswordInput } from "@/components/ui/passwordInput";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { LoadingSpinner } from "@/components/loading";
import { useDebouncedSearch } from "@/lib/hooks";
import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
import { Actions } from "@/components/ui/actions";
import { useResourceAccessPermission } from "@/lib/perms";
import { StatusBadge } from "@/components/ui/status-badge";
import { SeatsBadge } from "@/components/seats-badge";
import { useSeats } from "@/lib/queries/company";

const searchSchema = z.object({
  page: z.number().int().positive().default(1),
  per_page: z.number().int().positive().max(100).default(10),
  search: z.string().optional(),

  role_id: z.number().optional(),
  department_id: z.number().optional(),
  is_active: z.boolean().optional(),
});

export const Route = createFileRoute("/_app/users")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [stripSearchParams({ search: "" })],
  },

  loaderDeps: ({ search }) => search,
  loader({ context: { queryClient }, deps }) {
    queryClient.ensureQueryData(usersListQueryOptions(deps));
  },
  staticData: {
    permissionGuards: [
      {
        resource: "users",
      },
    ],
  },
});

const columns = [
  {
    accessorKey: "full_name",
    header: "Full Name",
    size: 60,
  },
  {
    accessorKey: "email",
    header: "Email",
    size: 60,
  },
  {
    header: "Role",
    accessorKey: "role.name",
    size: 60,
  },
  {
    header: "Department",
    accessorKey: "department.name",
    size: 60,
  },
  {
    accessorKey: "is_active",
    header: "Status",
    size: 60,
    cell: (info) => {
      const isActive = info.getValue() as boolean;
      return (
        <StatusBadge variant={isActive ? "success" : "default"}>
          {isActive ? "Active" : "Inactive"}
        </StatusBadge>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    size: 60,
    meta: { align: "right" },
    cell: ({ row: { original } }) => {
      const canEdit = useResourceAccessPermission("users", ["update"]);
      const canDelete = useResourceAccessPermission("users", ["delete"]);
      const canView = useResourceAccessPermission("users", ["read"]);

      return (
        <Actions>
          {canView.data && (
            <Button
              size="lg"
              variant="ghost"
              className="w-full justify-start text-base"
              asChild
            >
              <Link to="/users/$id" params={{ id: original.id }}>
                <EyeIcon className="size-5" />
                View
              </Link>
            </Button>
          )}
          {canEdit.data && (
            <EditDialog asChild user={original}>
              <Button
                size="lg"
                variant="ghost"
                className="w-full justify-start text-base"
              >
                <PencilIcon className="size-5" />
                Edit
              </Button>
            </EditDialog>
          )}
          {canDelete.data && <DeleteDialog user={original} />}
        </Actions>
      );
    },
  },
] as const satisfies Array<ColumnDef<User>>;

function RouteComponent() {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();

  const [searchQuery, setSearchQuery, rawSearchQuery] = useDebouncedSearch(
    Route.id,
    "search",
  );

  // FIXME: convert this to comboboxes once search is available for both roles and depts
  const roles = useRolesAll();
  const departments = useDepartmentsAll();

  const users = useUsersList({ ...searchParams, search: searchQuery });

  const canView = useResourceAccessPermission("users", ["read"]);
  const canEdit = useResourceAccessPermission("users", ["update"]);
  const canDelete = useResourceAccessPermission("users", ["delete"]);
  const canCreate = useResourceAccessPermission("users", ["create"]);

  const seats = useSeats();
  const seatsFull = Boolean(
    seats.data &&
      !seats.data.is_unlimited &&
      seats.data.max_seats > 0 &&
      seats.data.used_seats >= seats.data.max_seats,
  );

  const showActions = Boolean(canView.data || canEdit.data || canDelete.data);

  const table = useReactTable({
    data: users.data?.items ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
      columnVisibility: {
        actions: showActions,
      },
    },
    rowCount: users.data?.count ?? 1,

    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">Users</h1>
        <div className="flex gap-2">
          <ImportUsersButton />

          <SeatsBadge />

          {canCreate.data && (
            <CreateDialog asChild>
              <Button disabled={seatsFull}>
                <PlusIcon />
                Create User
              </Button>
            </CreateDialog>
          )}
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="relative w-full sm:w-64">
          <SearchIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name and email..."
            value={rawSearchQuery ?? ""}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
          <Select
            value={searchParams.role_id?.toString() ?? "all"}
            onValueChange={(value) =>
              navigate({
                to: ".",
                search: (s) => ({
                  ...s,
                  page: 1,
                  role_id: value === "all" ? undefined : parseInt(value),
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roles.data?.map((role) => (
                <SelectItem key={role.id} value={role.id.toString()}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={searchParams.department_id?.toString() ?? "all"}
            onValueChange={(value) =>
              navigate({
                to: ".",
                search: (s) => ({
                  ...s,
                  page: 1,
                  department_id: value === "all" ? undefined : parseInt(value),
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-[175px]">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.data?.map((department) => (
                <SelectItem
                  key={department.id}
                  value={department.id.toString()}
                >
                  {department.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={
              searchParams.is_active !== undefined
                ? String(searchParams.is_active)
                : "all"
            }
            onValueChange={(value) =>
              navigate({
                to: ".",
                search: (s) => ({
                  ...s,
                  page: 1,
                  is_active: value === "all" ? undefined : value === "true",
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="border-primary text-primary hover:bg-primary/10 hover:text-primary"
            onClick={() => {
              setSearchQuery("");
              navigate({
                to: ".",
                search: (old) => ({
                  page: old.page,
                  per_page: old.per_page,
                }),
              });
            }}
          >
            Reset Filters
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={users.isLoading} />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}

interface ImportUsersResponse {
  created_count: number;
  skipped_count: number;
  errors: Array<string>;
}

function ImportUsersButton() {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [showInfoDialog, setShowInfoDialog] = React.useState(false);
  const [showErrorsDialog, setShowErrorsDialog] = React.useState(false);
  const [importResult, setImportResult] =
    React.useState<ImportUsersResponse | null>(null);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.raw
        .post("api/v1/users/import", {
          body: formData,
        })
        .json<ImportUsersResponse>();
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
      queryClient.invalidateQueries({ queryKey: ["company", "seats"] });
      setImportResult(data);

      if (data.errors.length > 0) {
        setShowErrorsDialog(true);
        toast.warning(
          `Import completed with errors: ${data.created_count} created, ${data.skipped_count} skipped`,
        );
      } else {
        toast.success(
          `Successfully imported ${data.created_count} user${data.created_count !== 1 ? "s" : ""}`,
        );
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to import users: ${error.message}`);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (fileExtension !== "csv" && fileExtension !== "json") {
        toast.error("Please select a CSV or JSON file");
        event.target.value = "";
        return;
      }
      mutation.mutate(file);
    }
  };

  const downloadCSVTemplate = () => {
    const csvContent =
      "full_name,email,password,role_name,department_name\nJohn Doe,john@example.com,password123,Admin,Engineering";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadJSONTemplate = () => {
    const jsonContent = JSON.stringify(
      [
        {
          full_name: "John Doe",
          email: "john@example.com",
          password: "password123",
          role_name: "Admin",
          department_name: "Engineering",
        },
      ],
      null,
      2,
    );
    const blob = new Blob([jsonContent], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users_template.json";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.json"
        onChange={handleFileChange}
        className="hidden"
      />
      <Button
        variant="outline"
        onClick={() => setShowInfoDialog(true)}
        disabled={mutation.isPending}
      >
        <ImportIcon className="size-4" />
        {mutation.isPending ? "Importing..." : "Import Users"}
      </Button>

      <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Users</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV or JSON file containing user information. This will
              create new users in the system.
            </p>

            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/50 p-4">
                <p className="mb-2 text-sm font-medium">CSV Format:</p>
                <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                  <li>full_name: Full name of the user</li>
                  <li>email: Valid email address</li>
                  <li>password: Initial password</li>
                  <li>role_name: User's role</li>
                  <li>department_name: User's department</li>
                </ul>
                <Button
                  type="button"
                  variant="link"
                  className="mt-3 h-auto p-0 text-primary"
                  onClick={downloadCSVTemplate}
                >
                  <UploadIcon className="size-4" />
                  Download CSV template
                </Button>
              </div>

              <div className="rounded-lg border border-border bg-muted/50 p-4">
                <p className="mb-2 text-sm font-medium">JSON Format:</p>
                <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                  <li>Array of user objects with the same fields as CSV</li>
                  <li>full_name: Full name of the user</li>
                  <li>email: Valid email address</li>
                  <li>password: Initial password</li>
                  <li>role_name: User's role</li>
                  <li>department_name: User's department</li>
                </ul>
                <Button
                  type="button"
                  variant="link"
                  className="mt-3 h-auto p-0 text-primary"
                  onClick={downloadJSONTemplate}
                >
                  <UploadIcon className="size-4" />
                  Download JSON template
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowInfoDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowInfoDialog(false);
                  fileInputRef.current?.click();
                }}
              >
                <UploadIcon />
                Select File
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showErrorsDialog} onOpenChange={setShowErrorsDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Import Results</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg border p-3">
                    <div className="text-sm text-muted-foreground">Created</div>
                    <div className="text-2xl font-bold text-green-600">
                      {importResult?.created_count ?? 0}
                    </div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-sm text-muted-foreground">Skipped</div>
                    <div className="text-2xl font-bold text-yellow-600">
                      {importResult?.skipped_count ?? 0}
                    </div>
                  </div>
                </div>

                {importResult?.errors && importResult.errors.length > 0 && (
                  <div className="space-y-2">
                    <div className="font-semibold text-red-600">
                      Errors ({importResult.errors.length}):
                    </div>
                    <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border bg-muted p-3">
                      {importResult.errors.map((error, index) => (
                        <div key={index} className="text-sm">
                          {error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={() => setShowErrorsDialog(false)}>Close</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DeleteDialog({ user }: { user: User }) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn(id: string) {
      return api.raw.delete(`api/v1/users/${id}`);
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
      queryClient.invalidateQueries({ queryKey: ["company", "seats"] });
      queryClient.removeQueries({
        queryKey: ["users", "detail", user.id],
      });

      setOpen(false);
      toast.success(`User "${user.full_name}" deleted successfully`);
    },
    async onError(err) {
      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body?.detail) {
          message =
            typeof body.detail === "string"
              ? body.detail
              : (body.detail.message ?? message);
        }
      }
      toast.error(`Delete failed: ${message}`);
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="lg"
          variant="ghost"
          className="w-full justify-start text-base"
        >
          <TrashIcon className="size-5" />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="gap-3 p-4 sm:max-w-xs">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete User</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the user "{user.full_name}"? This
            action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending} asChild>
            <Button variant="outline">Cancel</Button>
          </AlertDialogCancel>

          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(user.id)}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditDialog({
  children,
  user,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean; user: User }>) {
  const [open, setOpen] = React.useState(false);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UserUpdateFormData;
    }) => {
      const response = await api.raw
        .put(`api/v1/users/${id}`, { json: data })
        .json<User>();
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
      queryClient.setQueryData(["users", "detail", data.id], data);

      setOpen(false);
      toast.success(`User "${data.full_name}" updated successfully`);
    },
    async onError(err) {
      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body?.detail) {
          message =
            typeof body.detail === "string"
              ? body.detail
              : (body.detail.message ?? message);
        }
      }
      toast.error(`Update failed: ${message}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent className="max-w-[calc(100%-1rem)]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <UserUpdateForm
            defaultValue={user}
            disabled={mutation.isPending}
            buttonText={mutation.isPending ? "Saving..." : "Update"}
            onSubmit={(data) => mutation.mutate({ id: user.id, data })}
            onCancel={() => setOpen(false)}
          />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({
  children,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean }>) {
  const [open, setOpen] = React.useState(false);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn(data: UserFormData) {
      return api.raw.post("api/v1/users/", { json: data }).json<User>();
    },
    onSuccess(data) {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
      queryClient.invalidateQueries({ queryKey: ["company", "seats"] });
      queryClient.setQueryData(["users", "detail", data.id], data);

      setOpen(false);
      toast.success(`User "${data.full_name}" created successfully`);
    },
    async onError(err) {
      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body?.detail) {
          message =
            typeof body.detail === "string"
              ? body.detail
              : (body.detail.message ?? message);
        }
      }
      toast.error(`Create failed: ${message}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent className="max-w-[calc(100%-1rem)]">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <UserCreateForm
            disabled={mutation.isPending}
            buttonText={mutation.isPending ? "Saving..." : "Create"}
            onSubmit={mutation.mutate}
            onCancel={() => setOpen(false)}
          />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

function UserCreateForm({
  disabled,
  buttonText,
  defaultValue,
  onSubmit,
  onCancel,
}: {
  disabled?: boolean;
  defaultValue?: UserFormData;
  buttonText: React.ReactNode;
  onSubmit: (data: UserFormData) => void;
  onCancel?: () => void;
}) {
  const form = useForm({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      ...generateDefaultValue(userFormSchema),
      ...defaultValue,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="full_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter full name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <PasswordInput placeholder="Enter password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex w-full gap-2">
          <FormField
            control={form.control}
            name="role_id"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel>Role</FormLabel>
                <FormControl>
                  <RoleSelectCombobox
                    value={field.value}
                    onValueChange={(id) => field.onChange(id)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="department_id"
            render={({ field }) => (
              <FormItem className="ml-auto w-1/2">
                <FormLabel>Department</FormLabel>
                <FormControl>
                  <DepartmentSelectCombobox
                    value={field.value}
                    onValueChange={(id) => field.onChange(id)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Active</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Enable or disable user account
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}

          <Button type="submit" disabled={disabled}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function UserUpdateForm({
  disabled,
  buttonText,
  defaultValue,
  onSubmit,
  onCancel,
}: {
  disabled?: boolean;
  defaultValue?: UserUpdateFormData;
  buttonText: React.ReactNode;
  onSubmit: (data: UserUpdateFormData) => void;
  onCancel?: () => void;
}) {
  const form = useForm({
    resolver: zodResolver(userUpdateFormSchema),
    defaultValues: {
      ...generateDefaultValue(userUpdateFormSchema),
      ...defaultValue,
      password: undefined,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="full_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter full name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password (leave empty to keep current)</FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder="Enter new password"
                  {...field}
                  onChange={(e) =>
                    e.target.value === ""
                      ? field.onChange(undefined) // treat empty string as unset
                      : field.onChange(e)
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex w-full gap-2">
          <FormField
            control={form.control}
            name="role_id"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel>Role</FormLabel>
                <FormControl>
                  <RoleSelectCombobox
                    value={field.value}
                    onValueChange={(id) => field.onChange(id)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="department_id"
            render={({ field }) => (
              <FormItem className="ml-auto w-1/2">
                <FormLabel>Department</FormLabel>
                <FormControl>
                  <DepartmentSelectCombobox
                    value={field.value}
                    onValueChange={(id) => field.onChange(id)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Active Status</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Enable or disable user account
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}

          <Button type="submit" disabled={disabled}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function RoleSelectCombobox({
  value: outerValue,
  onValueChange,
}: {
  value?: number;
  onValueChange?: (r: number | null) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [innerValue, setInnerValue] = React.useState<number | null>(null);

  const value = outerValue === undefined ? innerValue : outerValue;
  const setValue = React.useCallback(
    (id: number | null) => {
      setInnerValue(id);
      onValueChange?.(id);
    },
    [onValueChange],
  );

  // FIXME: replace with paginated search once search is implemented in paginated endpoint
  const roles = useRolesAll();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
        >
          {/* FIXME: find a better option later */}
          {roles.data?.find((r) => r.id === value)?.name ?? "Select role..."}
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder="Search role..." />
          {roles.isSuccess ? (
            <CommandList>
              <CommandEmpty>No roles found.</CommandEmpty>
              <CommandGroup>
                {roles.data.map((role) => (
                  <CommandItem
                    key={role.name}
                    value={role.id.toString()}
                    keywords={[role.description]}
                    onSelect={() => {
                      setValue(role.id === value ? null : role.id);
                      setOpen(false);
                    }}
                  >
                    <CheckIcon
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === role.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {role.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          ) : (
            <LoadingSpinner className="m-4 self-center" />
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DepartmentSelectCombobox({
  value: outerValue,
  onValueChange,
}: {
  value?: number;
  onValueChange?: (r: number | null) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [innerValue, setInnerValue] = React.useState<number | null>(null);

  const value = outerValue === undefined ? innerValue : outerValue;
  const setValue = React.useCallback(
    (r: number | null) => {
      setInnerValue(r);
      onValueChange?.(r);
    },
    [onValueChange],
  );

  // FIXME: replace with paginated search once search is implemented in paginated endpoint
  const departments = useDepartmentsAll();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
        >
          {/* FIXME: find better option later */}
          {departments.data?.find((d) => d.id === value)?.name ??
            "Select department..."}
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder="Search department..." />
          {departments.isSuccess ? (
            <CommandList>
              <CommandEmpty>No departments found.</CommandEmpty>
              <CommandGroup>
                {departments.data.map((department) => (
                  <CommandItem
                    key={department.name}
                    value={department.id.toString()}
                    keywords={[department.description]}
                    onSelect={() => {
                      setValue(department.id === value ? null : department.id);
                      setOpen(false);
                    }}
                  >
                    <CheckIcon
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === department.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {department.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          ) : (
            <LoadingSpinner className="m-4 self-center" />
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
